"use client";

import React, { useState } from 'react';
import ChatPanel from './ChatPanel';
import ThreatContextPanel from './ThreatContextPanel';
import ThreatAnalysisPanel from './ThreatAnalysisPanel';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { Bot, Sparkles, BarChart3, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import HeroBanner from '@/components/HeroBanner';
import { useTapRipple } from '@/hooks/useMobileInteractions';

type RightTab = 'context' | 'analysis';

export default function AIThreatAssistantContent() {
  const { language } = useLanguage();
  const [rightTab, setRightTab] = useState<RightTab>('analysis');
  const [mobileBottomOpen, setMobileBottomOpen] = useState(false);
  const { triggerRipple } = useTapRipple();

  return (
    <div className="flex flex-col swipe-in-right">
      <HeroBanner variant="compact" />

      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-cyber-blue/15 border border-cyber-blue/25 flex items-center justify-center">
            <Bot size={18} className="text-cyber-blue" />
          </div>
          <div>
            <h1 className={`text-lg md:text-xl font-bold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
              {t(language, 'ai_title')}
            </h1>
            <div className="flex items-center gap-1.5">
              <Sparkles size={11} className="text-cyber-blue" />
              <p className={`text-xs text-cyber-muted ${language === 'bn' ? 'font-bangla' : ''}`}>
                {t(language, 'ai_subtitle')}
              </p>
              <span className="text-[10px] font-mono text-cyber-green bg-cyber-green/10 border border-cyber-green/20 rounded-full px-2 py-0.5 ml-1">
                ONLINE
              </span>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-2 text-[11px] font-mono text-cyber-muted">
          <span className="w-1.5 h-1.5 bg-cyber-blue rounded-full live-dot" />
          <span>Gemini 1.5 Pro · Context: 128K tokens</span>
        </div>
      </div>

      {/* ── Desktop/Large Tablet Layout (xl+) ── */}
      <div className="hidden xl:flex gap-4 flex-1 min-h-0" style={{ height: 'calc(100vh - 3.5rem - 4rem - 120px)' }}>
        {/* Chat Panel — 70% */}
        <div className="flex-1 min-w-0">
          <ChatPanel />
        </div>

        {/* Right Panel — 30% with tab switcher */}
        <div className="flex flex-col w-80 2xl:w-96 shrink-0 gap-2">
          {/* Tab switcher */}
          <div className="flex items-center gap-1 bg-cyber-surface border border-cyber-border rounded-xl p-1 shrink-0">
            <button
              onClick={() => setRightTab('analysis')}
              onPointerDown={triggerRipple}
              aria-pressed={rightTab === 'analysis'}
              className={`ripple-container press-scale flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-[11px] font-mono transition-all duration-150 ${
                rightTab === 'analysis' ?'bg-cyber-blue/15 border border-cyber-blue/30 text-cyber-blue' :'text-cyber-muted hover:text-cyber-text'
              }`}
            >
              <BarChart3 size={11} />
              <span>AI Analysis</span>
            </button>
            <button
              onClick={() => setRightTab('context')}
              onPointerDown={triggerRipple}
              aria-pressed={rightTab === 'context'}
              className={`ripple-container press-scale flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg text-[11px] font-mono transition-all duration-150 ${
                rightTab === 'context' ?'bg-cyber-blue/15 border border-cyber-blue/30 text-cyber-blue' :'text-cyber-muted hover:text-cyber-text'
              }`}
            >
              <Layers size={11} />
              <span>Threat Context</span>
            </button>
          </div>

          {/* Panel content */}
          <div className="flex-1 min-h-0">
            {rightTab === 'analysis' ? <ThreatAnalysisPanel /> : <ThreatContextPanel />}
          </div>
        </div>
      </div>

      {/* ── Mobile/Tablet Layout (< xl) ── */}
      <div className="flex xl:hidden flex-col gap-3">
        {/* Chat Panel — full width */}
        <div className="w-full" style={{ height: 'calc(100vh - 3.5rem - 4rem - 200px)', minHeight: '320px' }}>
          <ChatPanel />
        </div>

        {/* Collapsible Panels Section */}
        <div className="border border-cyber-border rounded-xl overflow-hidden bg-cyber-surface">
          {/* Toggle Header */}
          <button
            onClick={() => setMobileBottomOpen(!mobileBottomOpen)}
            onPointerDown={triggerRipple}
            className="ripple-container press-scale w-full flex items-center justify-between px-4 py-3 bg-[#0A0D14] hover:bg-white/3 transition-colors"
            aria-expanded={mobileBottomOpen}
            aria-controls="mobile-ai-panels"
          >
            {/* Tab switcher inline */}
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); setRightTab('analysis'); if (!mobileBottomOpen) setMobileBottomOpen(true); }}
                onPointerDown={(e) => { e.stopPropagation(); triggerRipple(e); }}
                aria-pressed={rightTab === 'analysis'}
                className={`ripple-container press-scale-sm flex items-center gap-1.5 py-1 px-2.5 rounded-lg text-[11px] font-mono transition-all duration-150 ${
                  rightTab === 'analysis' ?'bg-cyber-blue/15 border border-cyber-blue/30 text-cyber-blue' :'text-cyber-muted hover:text-cyber-text border border-transparent'
                }`}
              >
                <BarChart3 size={11} />
                <span>AI Analysis</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setRightTab('context'); if (!mobileBottomOpen) setMobileBottomOpen(true); }}
                onPointerDown={(e) => { e.stopPropagation(); triggerRipple(e); }}
                aria-pressed={rightTab === 'context'}
                className={`ripple-container press-scale-sm flex items-center gap-1.5 py-1 px-2.5 rounded-lg text-[11px] font-mono transition-all duration-150 ${
                  rightTab === 'context' ?'bg-cyber-blue/15 border border-cyber-blue/30 text-cyber-blue' :'text-cyber-muted hover:text-cyber-text border border-transparent'
                }`}
              >
                <Layers size={11} />
                <span>Threat Context</span>
              </button>
            </div>
            {mobileBottomOpen
              ? <ChevronUp size={14} className="text-cyber-muted shrink-0" />
              : <ChevronDown size={14} className="text-cyber-muted shrink-0" />
            }
          </button>

          {/* Collapsible content */}
          <div
            id="mobile-ai-panels"
            className={`transition-all duration-300 overflow-hidden ${mobileBottomOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
          >
            <div className="p-3">
              {rightTab === 'analysis' ? <ThreatAnalysisPanel /> : <ThreatContextPanel />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}